import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getModulesForRole, splitModulesForTabs } from '../../modules/registry';
import NotificationBell from './NotificationBell';
import ToastContainer from './Toast';
import './PortalLayout.css';
import Sheet from './Sheet';
import Connectivity from './Connectivity';
import NavIcon from './NavIcon';
import { useEditStatus } from './EditProtection';

/**
 * App shell.
 *
 * The viewport is locked to 100dvh and never scrolls in either axis. Exactly
 * one region scrolls: .cs-app__content. The header and tab bar are flex
 * siblings carrying Liquid Glass — the only two elements in the app using
 * backdrop-filter — rather than fixed overlays.
 */
const PortalLayout: React.FC = () => {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const navigate = useNavigate();
  const editStatus = useEditStatus();
  const location = useLocation();
  const contentRef = useRef<HTMLElement>(null);
  const positions = useRef(new Map<string, number>());
  useLayoutEffect(() => { const content = contentRef.current; const savedPositions = positions.current; if (content) content.scrollTop = savedPositions.get(location.key) || 0; return () => { if (content) savedPositions.set(location.key, content.scrollTop); }; }, [location.key]);
  const [moreOpen, setMoreOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading…</p>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  const visibleModules = getModulesForRole(user.role);
  const { tabs, overflow } = splitModulesForTabs(visibleModules);

  const activeModule = visibleModules.find(
    (m) => location.pathname === m.basePath || location.pathname.startsWith(`${m.basePath}/`)
  );
  const subNav = activeModule?.subNav ?? [];
  const inOverflow = overflow.some((m) => m.key === activeModule?.key);

  const handleLogout = async () => {
    if (editStatus.dirty && !window.confirm('Discard unsaved changes and log out?')) return;
    editStatus.discard();
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="cs-app">
      <header className="cs-app__bar cs-glass">
        <div className="cs-app__brand">
          <img src="/logo-light.png" alt="" />
          <h1 className="cs-app__title">{activeModule?.label ?? 'Cafe Ops'}</h1>
        </div>
        <div className="cs-app__bar-actions">
          <NotificationBell />
          <span className="cs-app__who">{user.name}</span>
          <button className="cs-bar-btn" onClick={() => setAccountOpen(true)}>Account</button>
        </div>
      </header>

      <Connectivity />
      <div className="cs-app__body">
        <nav className="cs-app__sidebar" aria-label="Modules">
          {visibleModules.map((mod) => (
            <NavLink
              key={mod.key}
              to={mod.basePath}
              className={({ isActive }) => `cs-side-link${isActive ? ' active' : ''}`}
            >
              <span aria-hidden><NavIcon name={mod.key} /></span>
              <span>{mod.label}</span>
            </NavLink>
          ))}
        </nav>

        <main className="cs-app__content" ref={contentRef}>
          {subNav.length > 0 && (
            <nav className="cs-segmented" aria-label={`${activeModule?.label ?? ''} sections`}>
              {subNav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => `cs-segment${isActive ? ' active' : ''}`}
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          )}
          <Outlet />
        </main>
      </div>

      {/* Persistent destinations. Fixed set — never a horizontal scroller,
          which in this band competes with the Home and App Switcher gesture. */}
      <nav className="cs-app__tabs cs-glass" aria-label="Main">
        {tabs.map((mod) => (
          <NavLink
            key={mod.key}
            to={mod.basePath}
            className={({ isActive }) => `cs-tab${isActive ? ' active' : ''}`}
          >
            <span className="cs-tab__icon" aria-hidden><NavIcon name={mod.key} /></span>
            <span className="cs-tab__label">{mod.label}</span>
          </NavLink>
        ))}
        {overflow.length > 0 && (
          <button
            type="button"
            className={`cs-tab${inOverflow || moreOpen ? ' active' : ''}`}
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen(true)}
          >
            <span className="cs-tab__icon" aria-hidden>☰</span>
            <span className="cs-tab__label">More</span>
          </button>
        )}
      </nav>

      {/* Opens by tap, never by an edge swipe — a custom edge gesture on a
          phone competes with the OS back gesture and loses. */}
      {moreOpen && (
        <Sheet title="More" onClose={() => setMoreOpen(false)}>
          <nav aria-label="More modules">
            <div className="cs-drawer__grab" />
            {overflow.map((mod) => (
              <NavLink
                key={mod.key}
                to={mod.basePath}
                className={({ isActive }) => `cs-drawer__link${isActive ? ' active' : ''}`}
              >
                <span aria-hidden><NavIcon name={mod.key} /></span>
                <span>{mod.label}</span>
              </NavLink>
            ))}
          </nav>
        </Sheet>
      )}

      {accountOpen && <Sheet title="Account" onClose={() => setAccountOpen(false)}><p>{user.name} · {user.role}</p><div className="cs-actions"><NavLink to="/profile" onClick={() => setAccountOpen(false)}>My profile</NavLink><NavLink to="/help" onClick={() => setAccountOpen(false)}>Help</NavLink><button onClick={handleLogout}>Log out</button></div></Sheet>}
      <ToastContainer />
    </div>
  );
};

export default PortalLayout;
